import React from "react";
import { Link } from "react-router-dom";
import { colors, fonts } from "../theme";
import { categories, logos } from "../data";
import { brands } from "../brands";
import { Eyebrow, Btn, PhoneLink } from "../ui";
import Seo from "../../seo/Seo";
import { PAGE_META } from "../../seo/pageMeta";
import { absUrl } from "../../seo/site";
import { webPageSchema, breadcrumbSchema, itemListSchema } from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Brands", path: "/brands" },
];

const categoryName = (slug) => {
  const c = categories.find((x) => x.slug === slug);
  return c ? c.name : slug;
};

// The intrinsic pixel size of every logo file already lives in data.js, keyed
// by the same path `brand.logo` holds — it was added there for the home-page
// strip. Reusing it lets the browser reserve each logo's box before the file
// decodes, so a card's heading and blurb do not jump when it lands. Falls back
// to an empty object, which makes React drop the attributes entirely rather
// than emit width="undefined".
const logoBox = (src) => logos.find((l) => l.src === src) || {};

/**
 * The brands page.
 *
 * The home-page logo strip showed twelve mill logos and said nothing a search
 * engine could read, so a buyer searching "JK Paper dealer in Delhi" or
 * "Avery Dennison label stock Delhi" had no way to reach this business. This
 * page gives each brand real, verified text.
 *
 * Every fact here is checked against the manufacturer's own site (see
 * seo/brands-research.md), and the language is stockist-only throughout —
 * Sigma resells this material and is not an appointed distributor for any of
 * it. That distinction is a trademark matter, not a style preference.
 */
export default function Brands() {
  return (
    <main style={{ ...wrap, padding: "clamp(36px,8vw,60px) var(--sp-gutter) var(--sp-section-bot)" }}>
      <Seo
        title={PAGE_META.brands.title}
        description={PAGE_META.brands.description}
        path="/brands"
        schema={[
          webPageSchema({
            path: "/brands",
            name: PAGE_META.brands.title,
            description: PAGE_META.brands.description,
            type: "CollectionPage",
            breadcrumbId: `${absUrl("/brands")}#breadcrumb`,
          }),
          breadcrumbSchema(CRUMBS, "/brands"),
          itemListSchema({
            path: "/brands",
            name: "Paper and board brands stocked",
            items: brands.map((b) => ({ name: b.name, desc: b.blurb })),
          }),
        ]}
      />

      {/* se-crumbs lets the row wrap and gives each crumb a 44px box below
          700px — the links were 16px-tall targets on a line that could not
          break, so a long crumb split mid-label. */}
      <nav
        aria-label="Breadcrumb"
        className="se-crumbs"
        style={{ fontSize: "var(--fs-meta)", color: colors.muted2, marginBottom: "18px" }}
      >
        <Link to="/" style={{ color: colors.brown }}>Home</Link>
        <span style={{ margin: "0 8px" }}>/</span>
        <span aria-current="page">Brands</span>
      </nav>

      <Eyebrow>Mill brands</Eyebrow>
      <h1
        style={{
          fontFamily: fonts.serif,
          fontSize: "clamp(30px,6vw,46px)",
          fontWeight: 500,
          margin: "12px 0 18px",
          maxWidth: "22ch",
        }}
      >
        Paper &amp; Board Brands We Stock in Delhi
      </h1>

      <div style={{ maxWidth: "66ch", fontSize: "16.5px", lineHeight: 1.7, color: colors.ink3 }}>
        <p style={{ margin: "0 0 16px" }}>
          Sigma Enterprises has traded paper and board from Ramesh Nagar, New
          Delhi since 1996. This page lists the mills and brands our stock is
          drawn from, so a buyer can see at a glance whether the grade they are
          specifying is something we hold.
        </p>
        <p style={{ margin: "0 0 16px" }}>
          The list spans Indian mills — JK Paper, Century, Trident, Cosmo — and
          imported material from Indonesia and China, together with Avery
          Dennison self-adhesive face stocks. Between them they cover coated
          papers and boards, uncoated and printing papers, packaging board,
          specialty and synthetic stock, coloured sheets and label materials.
        </p>
        {/* This paragraph is a legal boundary, not filler. Do not soften it. */}
        <p style={{ margin: 0 }}>
          Sigma Enterprises is a wholesale stockist. We buy, hold and resell
          this material; we are not appointed by, and do not speak for, any of
          the manufacturers named here. Mill stock moves, so WhatsApp us on{" "}
          {/* A ~20px inline tap target mid-sentence, and the number could break
              across two lines. The padding builds a 44px box; the matching
              negative margin keeps the prose line rhythm unchanged. */}
          <PhoneLink
            style={{
              color: colors.brown,
              fontFamily: fonts.mono,
              whiteSpace: "nowrap",
              display: "inline-block",
              padding: "10px 6px",
              margin: "-10px -6px",
            }}
          />{" "}
          with the grade, grammage, size and quantity and we will tell you what
          is currently on the floor.
        </p>
      </div>

      <div style={{ marginTop: "52px", display: "flex", flexDirection: "column", gap: "18px" }}>
        {brands.map((b) => (
          <article
            key={b.slug}
            id={b.slug}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: "4px",
              background: colors.white,
              padding: "28px 30px",
              display: "grid",
              gridTemplateColumns: "150px 1fr",
              gap: "32px",
              alignItems: "start",
            }}
            className="se-brand-row se-panel"
          >
            <div
              style={{
                display: "grid",
                placeItems: "center",
                padding: "10px",
                border: `1px solid ${colors.borderSoft}`,
                borderRadius: "3px",
                background: "#FFFFFF",
                minHeight: "70px",
              }}
            >
              <img
                src={b.logo}
                alt={`${b.name} logo`}
                width={logoBox(b.logo).w}
                height={logoBox(b.logo).h}
                loading="lazy"
                decoding="async"
                style={{
                  maxWidth: "110px",
                  maxHeight: "48px",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>

            <div>
              <h2
                style={{
                  fontFamily: fonts.serif,
                  fontSize: "23px",
                  fontWeight: 600,
                  margin: "0 0 4px",
                }}
              >
                {b.name}
              </h2>
              {b.origin && (
                <div
                  style={{
                    fontFamily: fonts.mono,
                    // --fs-label (11px) is sized for a two-word chip. These
                    // origin lines run 55-90 characters of real prose — JK's
                    // is a four-mill list — so they read as body text and take
                    // the body floor. --fs-meta also lifts them off 11px mono
                    // at 0.06em tracking, which is the least legible
                    // combination on the page.
                    fontSize: "var(--fs-meta)",
                    letterSpacing: "0.04em",
                    lineHeight: 1.5,
                    color: colors.brown,
                    marginBottom: "12px",
                  }}
                >
                  {b.origin}
                </div>
              )}
              {/* These blurbs run 450-500 characters each and are the whole of
                  this page's body content, so they hold the 16px floor. */}
              <p
                style={{
                  fontSize: "var(--fs-body)",
                  lineHeight: 1.7,
                  color: colors.muted,
                  margin: "0 0 16px",
                  maxWidth: "72ch",
                }}
              >
                {b.blurb}
              </p>
              {/* Up to three of these per card across twelve cards, each a
                  16px-tall target with no box — the densest mis-tap zone on
                  the site. inline-flex + minHeight takes them to 44px without
                  changing how the row reads. */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
                {b.categorySlugs.map((slug) => (
                  <Link
                    key={slug}
                    to={`/catalogue/${slug}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      minHeight: "44px",
                      fontSize: "13.5px",
                      color: colors.brown,
                      cursor: "pointer",
                    }}
                  >
                    {categoryName(slug)} →
                  </Link>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Same pairing as the catalogue CTA: se-panel trims the nested padding
          below 560px, se-cta-band stretches the button to full width. */}
      <div
        className="se-cta-band se-panel"
        style={{
          marginTop: "56px",
          padding: "34px 32px",
          border: `1px solid ${colors.border}`,
          borderRadius: "4px",
          background: colors.panel,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "24px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: "16px", lineHeight: 1.6, color: colors.ink3, maxWidth: "54ch" }}>
          Looking for a mill or brand not listed here? We source a wider range on
          request — tell us the grade and we will find it.
        </div>
        {/* Override dropped: 13px on a 15px line box measured 42.9px, under
            the 44px floor Btn now carries by default. */}
        <Btn variant="dark" to="/contact" style={{ whiteSpace: "nowrap" }}>
          Send an enquiry →
        </Btn>
      </div>
    </main>
  );
}
